import { useState, useEffect, useMemo } from "react";
import { useOperatingHours } from "../../../hooks/gym/useOperatingHours";
import { ToggleButton } from "../../common/ui/toggleSwitch";
import type { DayOperatingHours } from "../../../hooks/requests/gym";

export const DAY_LABELS = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];
const DAY_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

const DEFAULT_OPERATING_HOURS: DayOperatingHours[] = Array.from(
  { length: 7 },
  () => ({ openHour: 6, closeHour: 23, closed: false }),
);

export const formatHour = (h: number) => `${h.toString().padStart(2, "0")}:00`;

const HourSelect = ({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
}) => (
  <select
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    disabled={disabled}
    className="px-2 py-1.5 text-size-xs bg-global-secondary text-global-primary border border-global-primary rounded-lg focus:outline-none focus:border-[var(--color-focus-border)] disabled:opacity-30 disabled:cursor-not-allowed min-w-[72px] appearance-none text-center"
  >
    {Array.from({ length: 24 }, (_, i) => (
      <option key={i} value={i}>
        {formatHour(i)}
      </option>
    ))}
  </select>
);

const hasInvalidHours = (hours: DayOperatingHours[]) =>
  hours.some((d) => !d.closed && d.closeHour <= d.openHour);

export const GymOperatingHours = () => {
  const {
    operatingHours: saved,
    isLoading,
    update,
    isUpdating,
  } = useOperatingHours();

  const [hours, setHours] = useState<DayOperatingHours[]>(
    DEFAULT_OPERATING_HOURS,
  );

  useEffect(() => {
    if (saved) {
      setHours(saved);
    }
  }, [saved]);

  const updateDay = (idx: number, patch: Partial<DayOperatingHours>) => {
    setHours((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    );
  };

  const hasChanges = useMemo(
    () =>
      JSON.stringify(saved ?? DEFAULT_OPERATING_HOURS) !==
      JSON.stringify(hours),
    [saved, hours],
  );

  const isValid = !hasInvalidHours(hours);

  const handleSave = () => {
    if (!isValid) return;
    update(hours);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-global-tertiary text-size-xs">
          Cargando horarios...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="text-size-sm font-semibold text-global-primary">
          Horarios de Operación
        </h4>
        <p className="text-size-2xs text-global-tertiary mt-1">
          Define los horarios de apertura y cierre de tu gimnasio para cada día
          de la semana.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        {hours.map((day, idx) => {
          const invalidRange = !day.closed && day.closeHour <= day.openHour;
          return (
            <div
              key={idx}
              className={`flex items-center gap-2 sm:gap-3 px-3 py-2 rounded-lg border transition-colors ${
                day.closed
                  ? "border-global-secondary bg-global-overlay-secondary opacity-60"
                  : invalidRange
                    ? "border-[var(--color-error-border)] bg-global-secondary"
                    : "border-global-primary bg-global-secondary"
              }`}
            >
              <span
                className="text-size-xs font-semibold text-global-primary w-5 shrink-0 sm:hidden"
                title={DAY_LABELS[idx]}
              >
                {DAY_SHORT[idx]}
              </span>
              <span className="text-size-xs font-semibold text-global-primary w-20 shrink-0 hidden sm:block">
                {DAY_LABELS[idx]}
              </span>

              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <HourSelect
                  value={day.openHour}
                  onChange={(v) => updateDay(idx, { openHour: v })}
                  disabled={day.closed}
                />
                <span className="text-size-2xs text-global-muted">—</span>
                <HourSelect
                  value={day.closeHour}
                  onChange={(v) => updateDay(idx, { closeHour: v })}
                  disabled={day.closed}
                />
              </div>

              <ToggleButton
                checked={!day.closed}
                onToggle={(open) => updateDay(idx, { closed: !open })}
              />
            </div>
          );
        })}
      </div>

      {hasChanges && (
        <div className="flex flex-col items-end gap-1.5">
          {!isValid && (
            <p className="text-size-2xs text-[var(--color-error-text)]">
              La hora de cierre debe ser posterior a la de apertura.
            </p>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid || isUpdating}
            className="btn-compact btn-global-primary rounded-lg text-size-xs font-medium disabled:opacity-50"
          >
            {isUpdating ? "Guardando..." : "Guardar"}
          </button>
        </div>
      )}
    </div>
  );
};
